import { useMemo, useState } from "react";

import { api, type FileItem } from "../api/client";
import { PreviewModal } from "../components/PreviewModal";
import { SelectionBar } from "../components/SelectionBar";
import { ThumbnailGrid } from "../components/ThumbnailGrid";
import { useDeleteFile } from "../hooks/useDeleteFile";
import { useFiles } from "../hooks/useFiles";

export function GalleryView({ isAdmin }: { isAdmin: boolean }) {
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [zipBusy, setZipBusy] = useState(false);
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const q = useFiles(order);
  const del = useDeleteFile();

  const onDelete = (f: FileItem) => {
    if (window.confirm(`Poistetaanko ${f.original_name}?`)) {
      del.mutate(f.id);
    }
  };

  const items = useMemo<FileItem[]>(
    () => (q.data?.pages ?? []).flatMap((p) => p.items),
    [q.data],
  );

  const enterSelect = (first?: FileItem) => {
    setSelectMode(true);
    if (first) setSelected(new Set([first.id]));
  };
  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };
  const toggleId = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = async () => {
    setSelectAllBusy(true);
    try {
      // Fetch any remaining pages so "all" truly covers every file.
      let pages = q.data?.pages ?? [];
      let hasNext = q.hasNextPage;
      while (hasNext) {
        const result = await q.fetchNextPage();
        pages = result.data?.pages ?? pages;
        hasNext = result.hasNextPage ?? false;
      }
      setSelected(new Set(pages.flatMap((p) => p.items).map((f) => f.id)));
    } finally {
      setSelectAllBusy(false);
    }
  };
  const downloadZip = async () => {
    if (selected.size === 0) return;
    setZipBusy(true);
    try {
      const blob = await api.zipDownload([...selected]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kuvat-${stamp()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      exitSelect();
    } finally {
      setZipBusy(false);
    }
  };

  return (
    <section className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-2 border-b border-ink/10 p-3">
        <h2 className="text-sm font-semibold">Galleria {q.isFetching ? "…" : ""}</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded border border-ink/30 px-3 py-1.5 text-sm"
            onClick={() => setOrder((o) => (o === "asc" ? "desc" : "asc"))}
          >
            {order === "asc" ? "Vanhimmat ensin" : "Uusimmat ensin"}
          </button>
          <button
            type="button"
            className="rounded border border-ink/30 px-3 py-1.5 text-sm"
            onClick={() => (selectMode ? exitSelect() : enterSelect())}
          >
            {selectMode ? "Peruuta" : "Valitse"}
          </button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto">
        {q.isLoading ? (
          <p className="p-6 text-center text-sm text-ink/60">Ladataan…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-ink/60">Ei vielä kuvia.</p>
        ) : (
          <ThumbnailGrid
            items={items}
            hasNextPage={!!q.hasNextPage}
            onLoadMore={() => q.fetchNextPage()}
            selectMode={selectMode}
            selectedIds={selected}
            onTileClick={(f) => {
              if (selectMode) toggleId(f.id);
              else {
                const idx = items.findIndex((x) => x.id === f.id);
                if (idx >= 0) setOpenIndex(idx);
              }
            }}
            onTileLongPress={(f) => {
              if (!selectMode) enterSelect(f);
            }}
            renderTileOverlay={
              isAdmin && !selectMode
                ? (f) => (
                    <span
                      role="button"
                      tabIndex={0}
                      className="absolute right-1 top-1 z-10 rounded bg-black/70 px-2 py-1 text-xs text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(f);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onDelete(f);
                        }
                      }}
                    >
                      Poista
                    </span>
                  )
                : undefined
            }
          />
        )}
      </div>
      {openIndex !== null && !selectMode && (
        <PreviewModal
          items={items}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onIndexChange={setOpenIndex}
        />
      )}
      {selectMode && (
        <SelectionBar
          count={selected.size}
          busy={zipBusy}
          selectAllBusy={selectAllBusy}
          onSelectAll={selectAll}
          onCancel={exitSelect}
          onDownload={downloadZip}
        />
      )}
    </section>
  );
}

function stamp(): string {
  const d = new Date();
  const z = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${z(d.getMonth() + 1)}${z(d.getDate())}-${z(d.getHours())}${z(d.getMinutes())}`;
}
